const DEEP_RESEARCH_MODELS = new Set([
  'competidor', 'competidor-2', 'competidor-3',
  'sabia-4', 'sabia-4-thinking', 'sabiazinho-4', 'sabia-5',
]);

export const supportsDeepResearch = (model: string): boolean =>
  DEEP_RESEARCH_MODELS.has(model);

// Todo mundo começa no Sabiá 5 (03/11/2026): contas @maritaca.ai sempre, sem
// seletor; as demais também, e conversas antigas seguem no modelo gravado até
// o servidor migrá-las para o modelo do plano (mesma família de histórico).
export const INTERNAL_CHAT_MODEL = 'sabia-5';
export const DEFAULT_CHAT_MODEL = 'sabia-5';

export const isInternalAccount = (email?: string): boolean =>
  !!email?.trim().toLowerCase().endsWith('@maritaca.ai');

export const chatModelFor = (email?: string): string =>
  isInternalAccount(email) ? INTERNAL_CHAT_MODEL : DEFAULT_CHAT_MODEL;

// Modelos que raciocinam antes de responder: o pré-pass sintético do
// sabia-4-thinking e o raciocínio nativo do Sabiá 5.
export const chatModelReasons = (model: string) =>
  model === 'sabia-4-thinking' || model === 'sabia-5';

// Esforço do raciocínio nativo (Sabiá 5), escolhido na barra de digitação.
export type ReasoningEffort = 'none' | 'low' | 'medium' | 'xhigh';
export const DEFAULT_REASONING_EFFORT: ReasoningEffort = 'low';
export const REASONING_EFFORTS: { value: ReasoningEffort; label: string; hint: string }[] = [
  { value: 'none', label: 'Sem raciocínio', hint: 'Responde direto, mais rápido' },
  { value: 'low', label: 'Baixo', hint: 'Pensa pouco antes de responder' },
  { value: 'medium', label: 'Médio', hint: 'Equilíbrio entre rapidez e cuidado' },
  { value: 'xhigh', label: 'Alto', hint: 'Pensa mais; melhor para tarefas difíceis' },
];
const REASONING_EFFORT_KEY = 'maritalk-reasoning-effort';

export const chatModelHasEffort = (model: string) => model === 'sabia-5';

export const reasoningEffortLabel = (effort: ReasoningEffort) =>
  REASONING_EFFORTS.find((e) => e.value === effort)?.label ?? effort;

export const savedReasoningEffort = (): ReasoningEffort | null => {
  try {
    const saved = localStorage.getItem(REASONING_EFFORT_KEY);
    if (REASONING_EFFORTS.some((e) => e.value === saved)) return saved as ReasoningEffort;
  } catch { /* storage bloqueado */ }
  return null;
};

export const loadReasoningEffort = (): ReasoningEffort => savedReasoningEffort() ?? DEFAULT_REASONING_EFFORT;

// Níveis que o PLANO libera: o YAML (api/app/chat_plans.yml) é a única fonte,
// servido por /api/chat/usage como reasoning_efforts + default_reasoning_effort.
// O YAML pode usar os aliases do engine (default/high -> xhigh, minimal -> low);
// a barra só mostra os níveis nativos. Sem plano carregado, mostra todos.
export type PlanEfforts = { reasoning_efforts?: string[]; default_reasoning_effort?: string } | null | undefined;
const EFFORT_ALIASES: Record<string, ReasoningEffort> = { default: 'xhigh', high: 'xhigh', minimal: 'low' };
export const nativeEffort = (value?: string | null): ReasoningEffort | null => {
  const raw = (value || '').trim().toLowerCase();
  const mapped = EFFORT_ALIASES[raw] ?? raw;
  return REASONING_EFFORTS.some((e) => e.value === mapped) ? (mapped as ReasoningEffort) : null;
};
export const planEffortOptions = (plan: PlanEfforts) => {
  const allowed = new Set((plan?.reasoning_efforts || []).map(nativeEffort).filter(Boolean));
  return allowed.size ? REASONING_EFFORTS.filter((e) => allowed.has(e.value)) : REASONING_EFFORTS;
};
// O padrão do plano, se a barra o mostra; senão o primeiro nível liberado na
// ordem do YAML (lá o primeiro é o padrão do tier); sem plano, o padrão fixo.
export const planDefaultEffort = (plan: PlanEfforts): ReasoningEffort => {
  const unlocked = (plan?.reasoning_efforts || []).map(nativeEffort).filter((e): e is ReasoningEffort => !!e);
  const wanted = nativeEffort(plan?.default_reasoning_effort);
  if (wanted && unlocked.includes(wanted)) return wanted;
  return unlocked[0] ?? DEFAULT_REASONING_EFFORT;
};
// Esforço a usar quando o plano chega: o lembrado, se o plano o libera; senão o padrão do plano.
export const effortForPlan = (plan: PlanEfforts, saved: ReasoningEffort | null): ReasoningEffort => {
  if (saved && planEffortOptions(plan).some((e) => e.value === saved)) return saved;
  return planDefaultEffort(plan);
};

export const saveReasoningEffort = (effort: ReasoningEffort) => {
  try { localStorage.setItem(REASONING_EFFORT_KEY, effort); } catch { /* ignora */ }
};

// O modelo pensa neste envio? No Sabiá 5 depende do esforço escolhido.
export const turnReasons = (model: string, effort: ReasoningEffort) =>
  chatModelReasons(model) && !(chatModelHasEffort(model) && effort === 'none');

// Nome do modelo mostrado na barra (só leitura).
const CHAT_MODEL_LABELS: Record<string, string> = {
  'sabia-5': 'S5',
  'sabia-4-thinking': 'Sabiá 4 Thinking',
  'sabia-4': 'Sabiá 4',
  'sabiazinho-4': 'Sabiazinho 4',
};
export const chatModelLabel = (model: string) => CHAT_MODEL_LABELS[model] ?? model;
